import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
// 128-px-Fassung: angezeigt mit 40 px, das Original (1024 px, 128 kB) waere
// auf dem Telefon der groesste Brocken vor dem ersten Bild.
import LogoImage from '../../assets/LOGO-128.png';
import { useTheme } from '../../contexts/ThemeContext';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { buttonClasses } from '../ui/Button';

/**
 * Kopf aller oeffentlichen Seiten (Landingpage, /start, /administration, FAQ,
 * Rechtsseiten, Werkstatt, Ueber mich). Pfad und Default-Export bleiben stabil:
 * Werkstatt-Tests mocken ihn ueber genau diesen Pfad.
 *
 * Vier Ziele, ein Knopf — kein Dropdown. Mobil ein Vollbild-Menue als natives
 * <dialog> (Fokusfalle und Escape vom Browser). Props werden ignoriert; aeltere
 * Aufrufer duerfen weiter welche uebergeben.
 *
 * Scroll-Erkennung lauscht im Capture-Modus am Dokument: Auf /, /start und
 * /administration scrollt das Fenster, auf allen anderen Seiten <body>
 * (index.css) — window.scrollY allein bliebe dort immer 0.
 */
// jsdom (Tests) kennt HTMLDialogElement nicht: dort zaehlt das Attribut.
const isDialogOpen = (d) => d.open === true || d.hasAttribute('open');

const NAV = [
  { label: "So geht's", to: { pathname: '/', hash: '#so-gehts' }, hash: true },
  { label: 'Für Schulen', to: '/start' },
  { label: 'Technik & Datenschutz', to: '/administration' },
  { label: 'FAQ', to: '/faq' },
];

const scrollTop = () =>
  Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, document.body.scrollTop || 0);

const Wordmark = () => {
  const { isDark } = useTheme();
  return (
    <Link to="/" className="flex items-center gap-2.5 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" aria-label="EachOtter – zur Startseite">
      <img
        src={LogoImage}
        alt=""
        width="40"
        height="40"
        className="w-9 h-9 sm:w-10 sm:h-10 object-contain"
        style={{ filter: isDark ? 'brightness(0) invert(1)' : 'none', opacity: isDark ? 0.92 : 1 }}
      />
      <span className="font-display uppercase tracking-[0.02em] text-lg sm:text-xl text-[rgb(var(--color-display-fill))]">
        EachOtter
      </span>
    </Link>
  );
};

const linkClass = ({ isActive }) =>
  `relative px-3 py-2 rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
    isActive ? 'text-foreground' : 'text-foreground/70 hover:text-foreground'
  }`;

const MobileMenu = ({ open, onClose }) => {
  const ref = useRef(null);
  useBodyScrollLock(open);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !isDialogOpen(dialog)) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    if (!open && isDialogOpen(dialog)) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;
    const onCancel = (event) => { event.preventDefault(); onClose(); };
    dialog.addEventListener('cancel', onCancel);
    return () => dialog.removeEventListener('cancel', onCancel);
  }, [onClose]);

  return createPortal(
    <dialog
      ref={ref}
      id="site-menu"
      aria-label="Menü"
      className="m-0 p-0 w-screen h-[100dvh] max-w-none max-h-none bg-background text-foreground backdrop:bg-transparent"
    >
      <div className="flex flex-col h-full" style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="flex items-center justify-between h-16 px-4 sm:px-6">
          <Wordmark />
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center w-11 h-11 -mr-2 rounded-xl text-foreground hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Menü schließen"
          >
            <X className="w-6 h-6" aria-hidden="true" />
          </button>
        </div>
        <nav aria-label="Hauptnavigation mobil" className="flex-1 px-4 sm:px-6 pt-6">
          <ul className="divide-y divide-border border-y border-border">
            {NAV.map((item) => (
              <li key={item.label}>
                <Link
                  to={item.to}
                  onClick={onClose}
                  className="flex items-center justify-between py-5 text-2xl font-semibold tracking-tight text-foreground rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="px-4 sm:px-6 pb-8">
          <Link to="/login" onClick={onClose} className={buttonClasses('secondary', 'lg', '', true)}>
            Anmelden
          </Link>
        </div>
      </div>
    </dialog>,
    document.body,
  );
};

const LandingHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    const onScroll = () => setScrolled(scrollTop() > 12);
    onScroll();
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  // Seitenwechsel schliesst das Menue.
  useEffect(() => { setMenuOpen(false); }, [location.pathname, location.hash]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-30 border-b transition-[background-color,border-color] duration-300 ${
        scrolled ? 'bg-background/85 backdrop-blur-md border-border/70' : 'bg-transparent border-transparent'
      }`}
      style={{ paddingTop: 'var(--native-safe-area-top, env(safe-area-inset-top, 0px))' }}
    >
      <div className="mx-auto max-w-7xl h-16 px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-6">
        <Wordmark />

        <nav aria-label="Hauptnavigation" className="hidden lg:flex items-center gap-1">
          {NAV.map((item) => (item.hash ? (
            <Link key={item.label} to={item.to} className={linkClass({ isActive: false })}>{item.label}</Link>
          ) : (
            <NavLink key={item.label} to={item.to} className={linkClass}>{item.label}</NavLink>
          )))}
          <Link to="/login" className={buttonClasses('secondary', 'sm', 'ml-4')}>Anmelden</Link>
        </nav>

        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 -mr-2 rounded-xl text-foreground hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label="Menü öffnen"
          aria-expanded={menuOpen}
          aria-controls="site-menu"
        >
          <Menu className="w-6 h-6" aria-hidden="true" />
        </button>
      </div>
      <MobileMenu open={menuOpen} onClose={closeMenu} />
    </header>
  );
};

export default LandingHeader;
